import type { QuoteBudgetEntryOptions, QuoteBudgetLine, QuoteBudgetPeopleCounts } from "../types";
import type { QuoteBudgetDrawerDraft, QuoteBudgetWorkspaceState } from "../types/localDraft";
import type { ManualEntryCategory, ManualEntryContext, ManualEntryDraft, ManualEntryVehicleType } from "../types/manualEntry";
import { chinaAreaRegions } from "../../../shared/region";
import { calculateCostBreakdown, isBusinessTaxLine } from "./quoteBudgetCalculationService";
import { formatManualEntries, normalizeBusDrawerDraft } from "./quoteBudgetManualEntryService";
import { buildManualInitialTemplate } from "./quoteBudgetManualTemplateService";
import { buildLineFormula } from "./quoteBudgetEditorViewService";
import { isRailTicketLine, railRolesUnchanged } from "./quoteBudgetRailAssignment";

/** 分类顶部与表内共用的角色均摊结果，缺价时金额仅包含已知费用。 */
export type RolePerCapitaSummary = {
  role: string;
  count: number;
  amount: string | null;
  status: "priced" | "pending" | "empty" | "no_people" | "not_paying";
};

/** 按学生及付费老师均摊全部普通费用，排除营业税并保留缺价状态。 */
export function buildRolePerCapitaSummary(lines: QuoteBudgetLine[], people: QuoteBudgetPeopleCounts, teacherPays = false): RolePerCapitaSummary[] {
  const ordinary = lines.filter(line => !isBusinessTaxLine(line));
  const priced = ordinary.filter(line => line.priceSourceType !== "unpriced");
  const summary = calculateCostBreakdown({ ...people, teacherPays }, priced, [], true);
  return [
    { role: "学生", count: people.studentCount, pays: true },
    { role: "老师", count: people.teacherCount, pays: teacherPays },
    { role: "导游", count: people.guideCount, pays: false },
    { role: "司机", count: people.driverCount, pays: false },
  ].map(({ role, count, pays }): RolePerCapitaSummary => {
    const status = !pays ? "not_paying" : count <= 0 || summary.shareCount <= 0 ? "no_people"
      : !ordinary.length ? "empty" : priced.length !== ordinary.length ? "pending" : "priced";
    return { role, count, status, amount: (status === "priced" || status === "pending") && priced.length ? summary.perCapitaAmount : null };
  });
}

/** 弹窗占用编辑位不等于有未保存内容；页面和缓存共用真实公式变更判断。 */
export function hasPendingWorkspaceFormula(workspace: QuoteBudgetWorkspaceState, lines: QuoteBudgetLine[]): boolean {
  const formula = workspace.formula;
  if (!formula) return false;
  const line = lines.find(item => item.budgetLineId === formula.lineId);
  if (formula.railFee) {
    if (formula.railFee.isNew) return Boolean(formula.railFee.name.trim() || formula.text.trim());
    const original = line?.entrySnapshot?.quantityMode === "linked_people" ? String(line.budgetUnitPrice) : `${line?.manualPeopleCount}人×${line?.budgetUnitPrice}元`;
    return !line || formula.railFee.name !== line.itemNameSnapshot || formula.railFee.role !== line.travelerRole || formula.text !== original;
  }
  return !line || (isRailTicketLine(line)
    ? !railRolesUnchanged(line, formula.railRoles)
    : formula.text !== buildLineFormula(line));
}

const CITIES = chinaAreaRegions.flatMap(province => province.cities.map(city => ({
  code: city.code, name: city.name,
  aliases: [city.name.replace(/市$/, ""), ...(province.cities.length === 1 ? [province.name, province.name.replace(/市$/, "")] : [])],
})));
const DRAWER_CATEGORY_NAMES: Record<ManualEntryCategory, string> = {
  stay: "住宿", point: "门票", meal: "餐", air: "飞机", ship: "船", bus: "大巴车", crew: "司导", misc: "杂费",
};

/** 抽屉解析和未确认判断共用的当前真实业务上下文。 */
export type ManualDrawerContextOptions = {
  lines: QuoteBudgetLine[];
  people: QuoteBudgetPeopleCounts;
  entryOptions: QuoteBudgetEntryOptions;
  dayCount?: number;
  vehicleTypes?: ManualEntryVehicleType[];
};

/** 为抽屉提供真实资源与替换范围外费用，与未确认判断使用同一来源。 */
export function buildManualDrawerContext(draft: QuoteBudgetDrawerDraft, options: ManualDrawerContextOptions): ManualEntryContext {
  const selectedPoint = options.entryOptions.points.find(point => point.id === draft.contextId);
  const replaced = options.lines.filter(line => draft.replaceIds.includes(line.budgetLineId));
  const groupIds = new Set(replaced.map(line => line.entrySnapshot?.groupId));
  return {
    category: draft.category, people: options.people,
    editingLines: draft.replaceAsNew ? undefined : replaced,
    areas: options.entryOptions.stayAreas.filter(area => area.id).map(area => ({
      id: area.id!, name: area.name, nights: area.nightCount,
      standards: area.items?.map(item => ({ name: item.skuName, spuId: item.spuId, skuId: item.skuId, role: item.accommodationRole })),
    })),
    areaId: draft.category === "stay" || draft.category === "meal" ? draft.contextId : undefined,
    point: selectedPoint?.id ? { ...selectedPoint, id: selectedPoint.id } : undefined,
    cities: CITIES, vehicleTypes: options.vehicleTypes ?? [], dayCount: options.dayCount,
    itineraryDays: options.entryOptions.itineraryDays, transports: options.entryOptions.transports,
    groupId: groupIds.size === 1 && !groupIds.has(undefined) ? replaced[0]?.entrySnapshot?.groupId : undefined,
    confirmedBusScopes: replaced.flatMap(line => line.resourceType === "包车" && line.entrySnapshot?.context
      ? [line.entrySnapshot.context] : []),
    remainingLines: options.lines.filter(line => !isBusinessTaxLine(line) && !draft.replaceIds.includes(line.budgetLineId)),
  };
}

/** 标签只使用唯一可确认的主体；范围有歧义时保留待确认说明。 */
function pendingDrawerLabel(draft: QuoteBudgetDrawerDraft, replaced: QuoteBudgetLine[] | undefined, options: ManualDrawerContextOptions): string {
  const category = DRAWER_CATEGORY_NAMES[draft.category];
  if (draft.replaceIds.length) {
    if (!replaced || replaced.some(line => isBusinessTaxLine(line) || workspaceCategory(line) !== draft.category)) return `${category} · 修改范围待确认`;
    const groups = buildWorkspaceGroups(replaced);
    return `${category} · ${groups.length === 1 ? groups[0].label : "批量修改"}`;
  }
  if (draft.category === "crew") return `${category} · 全行程`;
  if (!draft.contextId) return `${category} · 批量录入`;
  const contexts = draft.category === "point" ? options.entryOptions.points
    : draft.category === "stay" || draft.category === "meal" ? options.entryOptions.stayAreas : [];
  const matches = contexts.filter(item => item.id === draft.contextId);
  const label = matches.length === 1 ? matches[0].name.trim() : "";
  return `${category} · ${label || (draft.category === "point" ? "考察点待确认" : "区域待确认")}`;
}

/** 替换范围必须唯一且仍属于原费用分类；不得按金额或名称猜测新的替换目标。 */
export function getWorkspaceReplaceLines(draft: QuoteBudgetDrawerDraft, lines: QuoteBudgetLine[]): QuoteBudgetLine[] | undefined {
  const ids = new Set(draft.replaceIds);
  if (ids.size !== draft.replaceIds.length) return undefined;
  const replaced = lines.filter(line => ids.has(line.budgetLineId));
  if (replaced.length !== ids.size || new Set(replaced.map(line => line.budgetLineId)).size !== ids.size
    || replaced.some(line => isBusinessTaxLine(line) || workspaceCategory(line) !== draft.category)) return undefined;
  const byId = new Map(replaced.map(line => [line.budgetLineId, line]));
  return draft.replaceIds.map(id => byId.get(id)!);
}

/** 仅确定从未编辑的回填可自动清理；部分目标消失也会让整个旧替换范围失效。 */
function isStaleUntouchedReplaceDrawer(draft: QuoteBudgetDrawerDraft, lines: QuoteBudgetLine[]): boolean {
  const ids = new Set(draft.replaceIds);
  if (!ids.size || ids.size !== draft.replaceIds.length || draft.textEdited !== false) return false;
  const remaining = lines.filter(line => ids.has(line.budgetLineId));
  return remaining.length < ids.size && new Set(remaining.map(line => line.budgetLineId)).size === remaining.length
    && remaining.every(line => !isBusinessTaxLine(line) && workspaceCategory(line) === draft.category);
}

/** 同一次保存响应按明确身份更新抽屉引用，正文和编辑标记不变。 */
export function remapSavedWorkspace(
  state: QuoteBudgetWorkspaceState,
  submitted: QuoteBudgetLine[],
  saved: QuoteBudgetLine[],
): QuoteBudgetWorkspaceState {
  // 保存接口保留人工行的唯一 lineNo 和快照 groupId；只接受同次请求响应的一对一身份。
  const mapping = new Map<string, string>();
  for (const source of submitted) {
    if (!source.entrySnapshot || isBusinessTaxLine(source)) continue;
    const matches = saved.filter(line => line.lineNo === source.lineNo
      && line.entrySnapshot?.groupId === source.entrySnapshot?.groupId
      && line.resourceType === source.resourceType);
    if (submitted.filter(line => line.lineNo === source.lineNo).length === 1 && matches.length === 1
      && saved.filter(line => line.budgetLineId === matches[0].budgetLineId).length === 1) {
      mapping.set(source.budgetLineId, matches[0].budgetLineId);
    }
  }
  let changed = false;
  const drawers = Object.fromEntries(Object.entries(state.drawers).map(([key, draft]) => {
    const replaceIds = draft.replaceIds.map(id => mapping.get(id) ?? id);
    if (replaceIds.some((id, index) => id !== draft.replaceIds[index])) {
      changed = true;
      return [key, { ...draft, replaceIds }];
    }
    return [key, draft];
  }));
  return pruneStaleReplaceDrawers(changed ? { ...state, drawers } : state, saved);
}

/** 只有未编辑的失效回填可清理，真实输入始终留给用户处理。 */
export function pruneStaleReplaceDrawers(
  state: QuoteBudgetWorkspaceState,
  lines: QuoteBudgetLine[],
): QuoteBudgetWorkspaceState {
  let changed = false;
  const drawers = { ...state.drawers };
  for (const [key, draft] of Object.entries(drawers)) {
    if (isStaleUntouchedReplaceDrawer(draft, lines)) {
      delete drawers[key];
      changed = true;
    }
  }
  if (!changed) return state;
  const activeDrawerKey = state.activeDrawerKey && drawers[state.activeDrawerKey] ? state.activeDrawerKey : null;
  return { ...state, drawers, activeDrawerKey };
}

/** 返回需要保留并提示用户处理的抽屉内容，不修改原文或费用。 */
export function getPendingWorkspaceEntries(state: QuoteBudgetWorkspaceState, options: ManualDrawerContextOptions): Array<{ key: string; draft: QuoteBudgetDrawerDraft; label: string }> {
  return Object.entries(state.drawers).flatMap(([key, draft]) => {
    if (!draft.text.trim() && !(draft.category !== "misc" && draft.replaceIds.length && draft.textEdited)) return [];
    const context = buildManualDrawerContext(draft, options);
    const replaced = getWorkspaceReplaceLines(draft, options.lines);
    const contextKey = draft.category === "point" ? "pointId" : draft.category === "stay" || draft.category === "meal" ? "areaId" : undefined;
    if (draft.replaceIds.length) {
      // 保存后临时行 ID 被服务端替换、或目标已删：未手工编辑的修改缓存过期，不拦定稿。
      // 重复引用或同 ID 多行等歧义范围仍拦截，不能当成过期直接放行。
      if (!replaced) {
        if (isStaleUntouchedReplaceDrawer(draft, options.lines)) return [];
        return [{ key, draft, label: pendingDrawerLabel(draft, replaced, options) }];
      }
      // 新版与可安全回填的历史/自动明细：全文与当前明细回填一致即视为未改，不拦定稿。
      // 上下文归属复用入口匹配，预算资源 ID 不能冒充区域 ID；无法回填或正文已改仍拦截。
      if (replaced.every(line => !isBusinessTaxLine(line) && workspaceCategory(line) === draft.category)
        && (!draft.contextId || !contextKey || buildWorkspaceContextLines(
          draft.category, replaced, draft.category === "point" ? options.entryOptions.points : options.entryOptions.stayAreas,
        )[draft.contextId]?.length === replaced.length)
        && normalizeBusDrawerDraft(draft, options.lines).text === formatManualEntries(replaced, context)) return [];
    } else if (draft.textEdited !== true) {
      const contexts = draft.category === "point" ? options.entryOptions.points : options.entryOptions.stayAreas;
      const knownContext = !draft.contextId || !contextKey || contexts.filter(item => item.id === draft.contextId).length === 1;
      if (draft.initialText !== undefined ? draft.text === draft.initialText
        : knownContext && !(draft.category === "bus" && options.vehicleTypes === undefined) && draft.text === buildManualInitialTemplate(context)) return [];
    }
    return [{ key, draft, label: pendingDrawerLabel(draft, replaced, options) }];
  });
}

/** 新主表类别包含只读兼容的火车。 */
export type WorkspaceCategory = ManualEntryCategory | "rail";
/** 主表明确操作范围，不依赖显示名称做删除。 */
export type QuoteBudgetWorkspaceGroup = { id: string; label: string; category: WorkspaceCategory; lines: QuoteBudgetLine[] };
/** 区域／考察点入口费用统计：缺价与已确认免费分开计数。 */
export type WorkspaceContextStat = { lineCount: number; unpricedCount: number; freeCount: number };

/** 已确认单价为 0 视为免费；待补价不计入免费。 */
function isConfirmedFreeUnitPrice(line: QuoteBudgetLine): boolean {
  if (line.priceSourceType === "unpriced") return false;
  const price = Number(line.budgetUnitPrice);
  return Number.isFinite(price) && price === 0;
}

/** 按真实区域或考察点身份匹配费用，状态统计与入口修改共用同一归属规则。 */
export function buildWorkspaceContextLines(
  category: WorkspaceCategory,
  lines: QuoteBudgetLine[],
  contexts: Array<{ id?: string; name: string }>,
): Record<string, QuoteBudgetLine[]> {
  const matched = Object.fromEntries(contexts.map(context => [context.id ?? context.name, [] as QuoteBudgetLine[]]));
  if (category !== "stay" && category !== "meal" && category !== "point") return matched;
  const byId = new Map(contexts.filter(context => context.id).map(context => [context.id, context]));
  const byName = new Map<string, typeof contexts>();
  for (const context of contexts) byName.set(context.name, [...byName.get(context.name) ?? [], context]);
  for (const line of lines) {
    if (isBusinessTaxLine(line) || workspaceCategory(line) !== category) continue;
    const snapshot = line.entrySnapshot;
    const snapshotId = category === "point" ? snapshot?.context?.pointId : snapshot?.context?.areaId;
    // 旧区域行以 dim_stay_area 关联；新版住宿 SPU/SKU 属于住宿资源，不能当作区域身份。
    const legacyId = category === "point"
      ? !line.spuTableName || line.spuTableName === "resource_point_spu" ? line.spuId : undefined
      : line.spuTableName === "dim_stay_area" ? line.spuId : undefined;
    const identity = snapshotId || legacyId;
    let context = identity ? byId.get(identity) : undefined;
    if (!identity) {
      const names = [snapshot?.groupLabel, line.spuNameSnapshot].filter((name): name is string => Boolean(name));
      // 旧住宿／团餐定稿后转为预算资源名称；无人工快照时只兼容唯一的完整区域名。
      const legacySuffix = category === "stay" ? "住宿" : category === "meal" ? "团餐" : undefined;
      const matches = names.map(name => byName.get(name) ?? (
        legacySuffix && !snapshot && line.spuTableName === `resource_${category}_budget_spu` && name.endsWith(legacySuffix)
          ? byName.get(name.slice(0, -legacySuffix.length)) ?? [] : []
      ));
      const unique = matches.filter(items => items.length === 1).map(items => items[0]);
      if (unique.length && matches.every(items => items.length <= 1)
        && unique.every(item => item === unique[0])) context = unique[0];
    }
    if (!context) continue;
    matched[context.id ?? context.name].push(line);
  }
  return matched;
}

/** 按真实区域或考察点身份统计费用，缺少费用的候选也保留零初值。 */
export function buildWorkspaceContextStats(
  category: WorkspaceCategory,
  lines: QuoteBudgetLine[],
  contexts: Array<{ id?: string; name: string }>,
): Record<string, WorkspaceContextStat> {
  return Object.fromEntries(Object.entries(buildWorkspaceContextLines(category, lines, contexts)).map(([key, rows]) => [key, {
    lineCount: rows.length,
    unpricedCount: rows.filter(line => line.priceSourceType === "unpriced").length,
    freeCount: rows.filter(isConfirmedFreeUnitPrice).length,
  }]));
}

/** 入口状态：待录价红框、待补价、已确认免费与普通已录入互斥展示。 */
export function describeWorkspaceContextEntry(
  stats: WorkspaceContextStat,
  kind: "area" | "point",
): { entryState: "empty" | "pending" | "free" | "entered"; statusText: string } {
  if (!stats.lineCount) return { entryState: "empty", statusText: "待录价" };
  if (stats.unpricedCount) {
    return {
      entryState: "pending",
      statusText: kind === "point"
        ? `待补价·${stats.unpricedCount}项`
        : `${stats.lineCount}项 · ${stats.unpricedCount}项待补价`,
    };
  }
  if (stats.freeCount === stats.lineCount) {
    return {
      entryState: "free",
      statusText: kind === "point" ? `✓ 免费·${stats.lineCount}项` : `免费·${stats.lineCount}项`,
    };
  }
  return {
    entryState: "entered",
    statusText: kind === "point" ? `✓ 已录入·${stats.lineCount}项` : `${stats.lineCount}项`,
  };
}

/** 分类汇总金额为 0 时区分尚未录价、仍有待补价与已确认全部免费。 */
export function describeWorkspacePriceSummary(
  lines: QuoteBudgetLine[],
): { kind: "empty" | "pending" | "all_free" | "priced"; label: string | null } {
  const ordinary = lines.filter(line => !isBusinessTaxLine(line));
  if (!ordinary.length) return { kind: "empty", label: "待录价" };
  if (ordinary.some(line => line.priceSourceType === "unpriced")) return { kind: "pending", label: "待补价" };
  if (ordinary.every(isConfirmedFreeUnitPrice)) return { kind: "all_free", label: "全部免费" };
  return { kind: "priced", label: null };
}
/** 分类保留旧自动交通模式，不将火车或船混入飞机。 */
export function workspaceCategory(line: QuoteBudgetLine): WorkspaceCategory {
  if (line.resourceType === "住宿") return "stay";
  if (line.resourceType === "餐") return "meal";
  if (["门票", "考察点"].includes(line.resourceType)) return "point";
  if (["包车", "大巴车"].includes(line.resourceType)) return "bus";
  if (["导游", "导服", "司导"].includes(line.resourceType)) return "crew";
  if (line.resourceType === "大交通") {
    if (line.resourceEntryType === "船") return "ship";
    return line.resourceEntryType === "火车" || /火车|铁路/.test(line.itemSpecSnapshot ?? "") ? "rail" : "air";
  }
  return "misc";
}
/** 大巴按服务类型+天次合并用车组，使分行录入的多车型共享首列单元格。 */
function busRangeGroupKey(line: QuoteBudgetLine): string | null {
  const context = line.entrySnapshot?.context;
  if (!context?.serviceType || !context.dayNumbers?.length) return null;
  const days = context.dayNumbers.filter((day) => Number.isSafeInteger(day) && day > 0).sort((left, right) => left - right);
  return days.length ? `${context.serviceType}:${days.join(",")}` : null;
}

/**
 * 住宿／餐按区域身份合组，避免 V1 复制行因不同 priceSourceRef 拆成多组。
 * 优先快照 areaId，其次旧 dim_stay_area，再次组 ID，最后唯一区域名。
 */
function stayOrMealAreaGroupKey(line: QuoteBudgetLine, category: WorkspaceCategory): string | null {
  if (category !== "stay" && category !== "meal") return null;
  const snapshot = line.entrySnapshot;
  const areaId = snapshot?.context?.areaId?.trim();
  if (areaId) return `area:${areaId}`;
  if (line.spuTableName === "dim_stay_area" && line.spuId?.trim()) return `area:${line.spuId.trim()}`;
  if (snapshot?.groupId?.trim()) return snapshot.groupId.trim();
  const name = (snapshot?.context?.areaName || snapshot?.groupLabel || line.spuNameSnapshot || "").trim();
  return name ? `name:${name}` : null;
}

/**
 * 门票按考察点身份合组，避免 V1 复制行因不同 priceSourceRef 拆成多组。
 * 优先快照 pointId，其次 resource_point_spu，再次组 ID，最后唯一点名。
 */
function pointGroupKey(line: QuoteBudgetLine, category: WorkspaceCategory): string | null {
  if (category !== "point") return null;
  const snapshot = line.entrySnapshot;
  const pointId = snapshot?.context?.pointId?.trim();
  if (pointId) return `point:${pointId}`;
  if ((!line.spuTableName || line.spuTableName === "resource_point_spu") && line.spuId?.trim()) {
    return `point:${line.spuId.trim()}`;
  }
  if (snapshot?.groupId?.trim()) return snapshot.groupId.trim();
  const name = (snapshot?.context?.pointName || snapshot?.groupLabel || line.spuNameSnapshot || "").trim();
  return name ? `name:${name}` : null;
}

/**
 * 火车／飞机／船按城市路线合组，使同路线多角色共享「城市路线」单元格。
 * 优先快照 groupId；无快照时用路线名，避免仅因 priceSourceRef 不同拆开。
 * 手续费单独成组，不与票价路线合并。
 */
function transportRouteGroupKey(line: QuoteBudgetLine, category: WorkspaceCategory): string | null {
  if (category !== "rail" && category !== "air" && category !== "ship") return null;
  if (line.resourceEntryType === "手续费") return null;
  const snapshot = line.entrySnapshot;
  if (snapshot?.groupId?.trim()) return snapshot.groupId.trim();
  const label = (snapshot?.groupLabel || line.spuNameSnapshot || line.itemNameSnapshot || "").trim();
  return label ? `route:${category}:${label}` : null;
}

const MISC_BILLING_METHODS = [
  { key: "other", label: "其他费用" },
  { key: "total", label: "整项费用" },
] as const;
const MISC_ITEM_PRIORITY: Record<string, number> = { "保险": 0, "保险费": 0, "导游资金池": 1, "运营费": 2 };

/** 整项费用独立成区，其余计费方式共用其他费用区，不改写原计费快照。 */
export function miscBillingMethod(line: QuoteBudgetLine): { key: string; label: string } {
  const mode = line.entrySnapshot?.quantityMode;
  return MISC_BILLING_METHODS[mode === "total" || line.unit === "项" ? 1 : 0];
}

/** 同组共享操作范围；组名相同但身份不同必须分开。大巴同天次同服务多车型合并展示。 */
export function buildWorkspaceGroups(lines: QuoteBudgetLine[]): QuoteBudgetWorkspaceGroup[] {
  const groups = new Map<string, QuoteBudgetWorkspaceGroup>();
  for (const line of lines) {
    if (isBusinessTaxLine(line)) continue;
    const category = workspaceCategory(line);
    const snapshot = line.entrySnapshot;
    const areaKey = stayOrMealAreaGroupKey(line, category);
    const pointKey = pointGroupKey(line, category);
    const transportKey = transportRouteGroupKey(line, category);
    const miscMethod = category === "misc" ? miscBillingMethod(line) : null;
    const label = category === "stay" || category === "meal"
      ? (snapshot?.context?.areaName || snapshot?.groupLabel || line.spuNameSnapshot || line.itemNameSnapshot || line.resourceType)
      : category === "point"
        ? (snapshot?.context?.pointName || snapshot?.groupLabel || line.spuNameSnapshot || line.itemNameSnapshot || line.resourceType)
        : category === "misc"
          ? miscMethod!.label
          : (snapshot?.groupLabel || line.spuNameSnapshot || line.itemNameSnapshot || line.resourceType);
    const legacyIdentity = category === "crew" ? "all"
      : line.priceSourceRef || line.spuId || `${line.itemNameSnapshot ?? ""}:${line.resourceEntryType ?? ""}`;
    const busKey = category === "bus" ? busRangeGroupKey(line) : null;
    const miscKey = miscMethod ? miscMethod.key : null;
    const id = `${category}:${category === "crew" ? "all" : miscKey ?? busKey ?? areaKey ?? pointKey ?? transportKey ?? snapshot?.groupId ?? legacyIdentity}`;
    const group = groups.get(id) ?? { id, label: category === "crew" ? "全行程" : label, category, lines: [] };
    group.lines.push(line);
    groups.set(id, group);
  }
  const roleOrder = ["学生", "老师", "导游", "司机", "司陪"];
  const miscRank = Object.fromEntries(MISC_BILLING_METHODS.map((item, index) => [`misc:${item.key}`, index]));
  return [...groups.values()].map((group) => ({
    ...group,
    lines: [...group.lines].sort((left, right) => {
      if (group.category === "misc") {
        const priority = (line: QuoteBudgetLine) => MISC_ITEM_PRIORITY[(line.skuNameSnapshot || line.itemNameSnapshot || line.resourceEntryType || "").trim()] ?? 3;
        const difference = priority(left) - priority(right);
        if (difference) return difference;
      }
      if (group.category === "rail") {
        const feeRank = (line: QuoteBudgetLine) => line.entrySnapshot?.railFeeKind === "service" ? 1 : line.entrySnapshot?.railFeeKind === "custom" ? 2 : 0;
        const difference = feeRank(left) - feeRank(right);
        if (difference) return difference;
      }
      const nameOrder = (left.skuNameSnapshot || left.itemNameSnapshot || "").localeCompare(
        right.skuNameSnapshot || right.itemNameSnapshot || "", "zh-CN",
      );
      const rank = (role?: string | null) => {
        const index = roleOrder.indexOf(role ?? "");
        return index < 0 ? roleOrder.length : index;
      };
      return nameOrder || rank(left.travelerRole) - rank(right.travelerRole) || left.lineNo - right.lineNo;
    }),
  })).sort((left, right) => {
    if (left.category === "misc" && right.category === "misc") {
      return (miscRank[left.id] ?? 99) - (miscRank[right.id] ?? 99);
    }
    return 0;
  });
}

/** 入口排序键：可选限定在当前天段内的最早有效天次，无天次为 0（升序时置后）。 */
function pointDaySortKey(point: QuoteBudgetEntryOptions["points"][number], range?: { start: number; endExclusive: number }): number {
  const days = (point.dayNumbers ?? []).filter(day => Number.isSafeInteger(day) && day > 0
    && (!range || (day >= range.start && day < range.endExclusive)));
  return days.length ? Math.min(...days) : 0;
}

/** 考察点入口按天数升序；同天保持原相对顺序，无天次排在最后。 */
export function sortPointsByDayAsc(
  points: QuoteBudgetEntryOptions["points"],
  range?: { start: number; endExclusive: number },
): QuoteBudgetEntryOptions["points"] {
  return points
    .map((point, index) => ({ point, index, day: pointDaySortKey(point, range) }))
    .sort((left, right) => {
      if (left.day === 0 && right.day === 0) return left.index - right.index;
      if (left.day === 0) return 1;
      if (right.day === 0) return -1;
      return left.day - right.day || left.index - right.index;
    })
    .map(item => item.point);
}

/** 从费用组首行解析考察点身份，优先快照 pointId，再回退旧版 SPU。 */
function pointIdentityFromGroup(group: QuoteBudgetWorkspaceGroup): { id?: string; name: string } {
  const line = group.lines[0];
  const snapshotId = line?.entrySnapshot?.context?.pointId;
  const legacyId = line && (!line.spuTableName || line.spuTableName === "resource_point_spu")
    ? line.spuId || undefined : undefined;
  return { id: snapshotId || legacyId || undefined, name: group.label };
}

/** 从费用组解析区域／考察点身份；非入口类返回 null。 */
export function workspaceGroupContextIdentity(
  group: QuoteBudgetWorkspaceGroup,
): { id?: string; name: string } | null {
  if (group.category === "point") return pointIdentityFromGroup(group);
  if (group.category !== "stay" && group.category !== "meal") return null;
  const line = group.lines[0];
  const snapshotId = line?.entrySnapshot?.context?.areaId;
  const legacyId = line?.spuTableName === "dim_stay_area" ? line.spuId || undefined : undefined;
  return { id: snapshotId || legacyId || undefined, name: group.label };
}

/** 风险筛选时隐藏已录入／免费（变绿）入口对应的费用组。 */
export function filterOutCompletedContextGroups(
  groups: QuoteBudgetWorkspaceGroup[],
  contextStats: Record<string, WorkspaceContextStat>,
): QuoteBudgetWorkspaceGroup[] {
  return groups.filter((group) => {
    const identity = workspaceGroupContextIdentity(group);
    if (!identity) return true;
    const stats = contextStats[identity.id ?? identity.name] ?? { lineCount: 0, unpricedCount: 0, freeCount: 0 };
    const entry = describeWorkspaceContextEntry(stats, group.category === "point" ? "point" : "area");
    return entry.entryState !== "entered" && entry.entryState !== "free";
  });
}

/** 门票费用组按考察点天数升序，规则与入口卡片一致；非门票组保留原相对位置。 */
export function sortPointWorkspaceGroupsByDayAsc(
  groups: QuoteBudgetWorkspaceGroup[],
  points: QuoteBudgetEntryOptions["points"],
): QuoteBudgetWorkspaceGroup[] {
  const byId = new Map(points.filter(point => point.id).map(point => [point.id!, point]));
  const byName = new Map<string, QuoteBudgetEntryOptions["points"]>();
  for (const point of points) byName.set(point.name, [...byName.get(point.name) ?? [], point]);
  const dayFor = (group: QuoteBudgetWorkspaceGroup): number => {
    const identity = pointIdentityFromGroup(group);
    let point = identity.id ? byId.get(identity.id) : undefined;
    if (!point) {
      const matches = byName.get(identity.name) ?? [];
      if (matches.length === 1) point = matches[0];
    }
    return point ? pointDaySortKey(point) : 0;
  };
  const pointSlots = groups
    .map((group, index) => ({ group, index }))
    .filter(item => item.group.category === "point");
  if (!pointSlots.length) return groups;
  const sortedPoints = pointSlots
    .map((item, order) => ({ ...item, order, day: dayFor(item.group) }))
    .sort((left, right) => {
      if (left.day === 0 && right.day === 0) return left.order - right.order;
      if (left.day === 0) return 1;
      if (right.day === 0) return -1;
      return left.day - right.day || left.order - right.order;
    })
    .map(item => item.group);
  let next = 0;
  return groups.map(group => group.category === "point" ? sortedPoints[next++]! : group);
}

/** 超过十二点时按真实五天天段组织入口；未知天次独立保留；组内天数升序。 */
export function buildPointDayGroups(points: QuoteBudgetEntryOptions["points"]): Array<{ key: string; label: string; points: QuoteBudgetEntryOptions["points"] }> {
  if (points.length <= 12) return [];
  const days = points.flatMap(point => point.dayNumbers ?? []).filter(day => Number.isSafeInteger(day) && day > 0);
  const starts = [...new Set(days.map(day => Math.floor((day - 1) / 5) * 5 + 1))].sort((left, right) => left - right);
  const lastDay = Math.max(0, ...days);
  const groups = starts.map(start => ({
    key: String(start), label: start === Math.min(start + 4, lastDay) ? `第${start}天` : `第${start}～${Math.min(start + 4, lastDay)}天`,
    points: sortPointsByDayAsc(
      points.filter(point => point.dayNumbers?.some(day => Number.isSafeInteger(day) && day >= start && day < start + 5)),
      { start, endExclusive: start + 5 },
    ),
  }));
  const unknown = points.filter(point => !point.dayNumbers?.some(day => Number.isSafeInteger(day) && day > 0));
  if (unknown.length) groups.push({ key: "unknown", label: "天次待确认", points: unknown });
  return groups;
}
/** 本点快捷项只追加模板，已录入状态不会删除或修改费用。 */
export function buildPointSkuShortcuts(point: QuoteBudgetEntryOptions["points"][number], lines: QuoteBudgetLine[], drafts: ManualEntryDraft[] = [], replaceIds: string[] = []): Array<{ key: string; label: string; text: string; entered: boolean; disabledReason?: string }> {
  const pointLines = lines.filter(line => !isBusinessTaxLine(line) && workspaceCategory(line) === "point"
    && Boolean(point.id) && (line.entrySnapshot?.context?.pointId
      ?? ((!line.spuTableName || line.spuTableName === "resource_point_spu") ? line.spuId : null)) === point.id);
  const candidates: Array<{ skuId?: string; name: string; role: string | null }> = (point.items ?? []).filter(item => item.skuName.trim()).map(item => ({
    skuId: item.skuId, name: item.skuName, role: item.travelerRole === "teacher" ? "老师" : "学生",
  }));
  for (const line of pointLines) {
    if (line.entrySnapshot?.inputMode !== "manual_v2") continue;
    const name = line.itemNameSnapshot || line.skuNameSnapshot;
    const role = line.travelerRole || null;
    if (!name || candidates.some(item => item.name === name && item.role === role)) continue;
    candidates.push({ skuId: line.skuId || undefined, name, role });
  }
  const confirmed = pointLines.filter(line => !replaceIds.includes(line.budgetLineId));
  return candidates.map(item => {
    const disabledReason = item.name.length > 40 || /[\s;；,，、<>]/u.test(item.name)
      ? "名称须在40字以内且不含空格或分隔符，请先在资源维护中调整"
      : candidates.filter(other => other.name === item.name && other.role === item.role).length > 1
        ? "同名同角色存在多个 SKU，请先在资源维护中区分名称" : undefined;
    const matches = (skuId: string | null | undefined, name: string | undefined, role: string | null | undefined) =>
      (role || null) === item.role && (item.skuId && skuId ? item.skuId === skuId : item.name === name);
    return {
      key: item.skuId || `${item.name}:${item.role ?? "session"}`,
      label: item.role ? item.name.startsWith(item.role) ? item.name : `${item.role}${item.name}` : `${item.name}（按场次）`,
      text: `#${item.name} ${item.role ? `${item.role} 1×0` : "1×0"}`, disabledReason,
      entered: confirmed.some(line => matches(line.skuId, line.itemNameSnapshot || line.skuNameSnapshot, line.travelerRole))
        || drafts.some(row => matches(row.skuId, row.name, row.role)),
    };
  });
}
